<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import Section from "@components/Section.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import Media from "@components/common/Media.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import logoSolutionHorizontalGray from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo+solution-grayscale-rgb.svg";
	import logoSolutionHorizontalLightBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo+solution-pro-svetle-pozadi-rgb.svg";
	import logoSolutionHorizontalDarkBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo+solution-pro-tmave-pozadi-rgb.svg";
	import logoSolutionHorizontalReverse from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo+solution-reverse-rgb.svg";
	import logoHorizontalGray from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo-grayscale-rgb.svg";
	import logoHorizontalLightBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo-pro-svetle-pozadi-rgb.svg";
	import logoHorizontalDarkBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo-pro-tmave-pozadi-rgb.svg";
	import logoHorizontalReverse from "@core/assets/images/fastest-logo-variants/fastest-zakladni-horizontal-logo-reverse-rgb.svg";
	import logoSolutionVerticalGray from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo+solution-grayscale-rgb.svg";
	import logoSolutionVerticalLightBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo+solution-pro-svetle-pozadi-rgb.svg";
	import logoSolutionVerticalDarkBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo+solution-pro-tmave-pozadi-rgb.svg";
	import logoSolutionVerticalReverse from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo+solution-reverse-rgb.svg";
	import logoVerticalGray from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo-grayscale-rgb.svg";
	import logoVerticalLightBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo-pro-svetle-pozadi-rgb.svg";
	import logoVerticalDarkBackground from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo-pro-tmave-pozadi-rgb.svg";
	import logoVerticalReverse from "@core/assets/images/fastest-logo-variants/fastest-zakladni-vertical-logo-reverse-rgb.svg";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import type { TechnologiesProps } from "./TechnologiesProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: TechnologiesProps = $props();
</script>

<Layout>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Vizuální identita" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted text="Seznamte se s novou vizuální identitou Fastest Solution." />
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href="/fastestsolution-logo-2025.zip"
				download
			>
				<Formatted text="Stáhnout fastest logo" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section centered paddingFollowNext>
		{#snippet headline()}
			<Formatted text="Barevnost" />
		{/snippet}
	</Section>

	<div class={[Background.White, "full-width text-center py-28"]}>
		<span class="font-loos-compressed tracking-[1em] text-lg">
			<Formatted text="#FFFFFF" />
		</span>
	</div>
	<div class={[Background.Green, "full-width text-center py-28"]}>
		<span class="font-loos-compressed tracking-[1em] text-lg">
			<Formatted text="#44D62C" />
		</span>
	</div>
	<div class={[Background.Phlox, "full-width text-center py-28 text-white"]}>
		<span class="font-loos-compressed tracking-[1em] text-lg">
			<Formatted text="#E132E1" />
		</span>
	</div>
	<div class={[Background.Onyx, "full-width text-center py-28 text-white"]}>
		<span class="font-loos-compressed tracking-[1em] text-lg">
			<Formatted text="#212020" />
		</span>
	</div>

	<Section centered noPadding="bot">
		{#snippet headline()}
			<Formatted text="Logo" />
		{/snippet}
	</Section>

	<Section>
		<div class="grid grid-cols-1 lg:grid-cols-2">
			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoHorizontalGray, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoHorizontalLightBackground, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoHorizontalReverse, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoHorizontalDarkBackground, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionHorizontalGray, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionHorizontalLightBackground, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionHorizontalReverse, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionHorizontalDarkBackground, type: "image/svg+xml" }}
					width={208}
					height={43.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoVerticalGray, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoVerticalLightBackground, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoVerticalReverse, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoVerticalDarkBackground, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>

			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionVerticalGray, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.White]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionVerticalLightBackground, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionVerticalReverse, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>
			<div class={["py-16", Background.Onyx]}>
				<Media
					class={["h-auto w-52 lg:w-96 mx-auto"]}
					source={{ src: logoSolutionVerticalDarkBackground, type: "image/svg+xml" }}
					width={300}
					height={216.5}
					alt="Fastest Solution logo"
				/>
			</div>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Tyson], contacts[BookMeetingPerson.Kucera]]} />
</Layout>
